import { CalculationRowType, VatEntry, VatRate } from "../../model";
import { EnumerationsLogic, Translations, ExpensesVatRate } from "../../logic";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { ExpensesUsecase } from "../ExpensesUsecase";
import { UsecaseRow } from "../UsecaseRow";
import { Numeric, Years, Dates, DatelyObject } from "../../util";

/**
 * Client-side usecases logic for Expenses as well as PrepaidExpenses.
 */
export class ExpensesUsecaseLogic extends BaseUsecaseLogic {
  private _hasGrossVatEntries: boolean;

  private _row: UsecaseRow<CalculationRowType.Expenses, ExpensesUsecase>;
  private _date: DatelyObject;

  /** When user is editing rows (varRate = "multiple"), this is the filed that is being edited => source for calculation. */
  public multipleRatesEditField : "tax" | "gross" | "net" = "gross";

  /** Returns true if the row has gross vatEntries */
  public get hasGrossVatEntries(): boolean {
    return this._hasGrossVatEntries ?? false;
  }

  /**
   * Updates the usecase values based on user input without going to the server.
   */
  public updateUsecase(row: UsecaseRow<CalculationRowType.Expenses, ExpensesUsecase>, date: DatelyObject): void {
    row = row ?? this._row;
    this._row = row;
    date = date ?? this._date;
    this._date = date;

    const vatRate = row.data.vatRate;
    if (vatRate == ExpensesVatRate.Other) {
      if (row.accounting.vatPercent == null) {
        row.accounting.vatPercent = Years.getYearlyChangingNumbers(date ?? Dates.getToday()).sideCosts.palkkausVatPercent;
      }
      this.taxRates["other"] = row.accounting.vatPercent;
    } else {
      row.accounting.vatPercent = null;
      this.taxRates["other"] = null;
    }

    const taxPercent = this.taxRates[vatRate];
    if (taxPercent != null) {
      row.accounting.vatEntries = [this.getRow(row.count * row.price, vatRate)];
      row.data.total = null;
    } else if (vatRate == ExpensesVatRate.Multiple) {
      if (!row.accounting.vatEntries) {
        row.accounting.vatEntries = this.getEmptyVatEntries();
      } else {
        //UI sends full array of VAT entries, but API wrapper
        //send likely only rows that have some gross value.
        //UI can handle only full array, so this code fills missing empty cells.
        const entries = this.getEmptyVatEntries();
        row.accounting.vatEntries.forEach((r) => {
          //VAT rates have their place on the UI. 24% is first.
          switch (r.vatRate) {
            case VatRate.Vat2400:
              entries[0] = r;
              break;
            case VatRate.Vat1400:
              entries[1] = r;
              break;
            case VatRate.Vat1000:
              entries[2] = r;
              break;
            case VatRate.Vat0000:
              entries[3] = r;
              break;
            case VatRate.Vat2550:
              entries[4] = r;
              break;
          }
        });
        row.accounting.vatEntries = entries;
      }


      const totalRow: VatEntry = {
        gross: 0,
        net: 0,
        tax: 0,
        taxPercent: 0,
        vatRate: VatRate.Undefined,
      };
      row.accounting.vatEntries.forEach((r) => {
        switch (this.multipleRatesEditField) {
          case "net":
            totalRow.net += r.net = Numeric.round(r.net);
            totalRow.tax += r.tax = Numeric.round(r.net * r.taxPercent);
            totalRow.gross += r.gross = r.net + r.tax;
            break;
          case "tax":
            totalRow.tax += r.tax = Numeric.round(r.tax);
            totalRow.net += r.net = Numeric.round(r.tax / r.taxPercent);
            totalRow.gross += r.gross = r.net + r.tax;
            break;
          case "gross":
          default:
            totalRow.gross += r.gross = Numeric.round(r.gross);
            totalRow.net += r.net = Numeric.round(r.gross / (1 + r.taxPercent));
            totalRow.tax += r.tax = r.gross - r.net;
            break;
        }
      });
      totalRow.taxPercent = Numeric.round(totalRow.tax / totalRow.net);
      row.data.total = totalRow;
    }
    if(row.data.vatRate === ExpensesVatRate.Multiple) {
      this._hasGrossVatEntries = !!row.accounting.vatEntries?.find((x) => x.gross);
    }
  }

  private getEmptyVatEntries(): VatEntry[] {
    return [
      this.getRow(null, ExpensesVatRate.Tax24),
      this.getRow(null, ExpensesVatRate.Tax14),
      this.getRow(null, ExpensesVatRate.Tax10),
      this.getRow(null, ExpensesVatRate.Tax0),
      this.getRow(null, ExpensesVatRate.Tax255),
    ];
  }



  private getRow(gross: number, vatRate: ExpensesVatRate): VatEntry {
    if (gross) {
      gross = Numeric.round(gross);
    }
    const taxPercent = this.taxRates[vatRate] || 0;
    const net = gross ? Numeric.round(gross / (1 + taxPercent)) : null;

    const taxRateMapper = {
      tax0: VatRate.Vat0000,
      tax10: VatRate.Vat1000,
      tax14: VatRate.Vat1400,
      tax24: VatRate.Vat2400,
      tax255: VatRate.Vat2550,
      none: VatRate.None,
      other: VatRate.Other,
    };

    return {
      gross,
      tax: gross ? Numeric.round(gross - net) : undefined,
      net: net,
      taxPercent,
      vatRate: taxRateMapper[vatRate] ?? VatRate.None,
    };
  }

  /** Taxrates and their number percentages as a key-value pair. Values are decimal: 0-1 as 0-100 percent. */
  public taxRates = {
    tax0: 0,
    tax10: 0.10,
    tax14: 0.14,
    tax24: 0.24,
    tax255: 0.255,
    none: 0,
    other: null,
  };

  /** Gets validation message for BoardUsecaseLogic  */
  public getUcValidation(row: UsecaseRow<CalculationRowType.Expenses, ExpensesUsecase>): UsecaseValidationMessage {
    let msg = "SALAXY.VALIDATION.Usecases.expensesValidation.noVat";
    switch (row.data.vatRate) {
      case ExpensesVatRate.Tax0:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat0";
        break;
      case ExpensesVatRate.Tax10:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat10";
        break;
      case ExpensesVatRate.Tax14:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat14";
        break;
      case ExpensesVatRate.Tax24:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat24";
        break;
      case ExpensesVatRate.Tax255:
        msg = "SALAXY.VALIDATION.Usecases.expensesValidation.vat255";
        break;
      case ExpensesVatRate.Multiple:
        msg = EnumerationsLogic.getEnumLabel("ExpensesVatRate", ExpensesVatRate.Multiple);
        break;
      case ExpensesVatRate.Other:
        msg = Translations.get("SALAXY.VALIDATION.Usecases.expensesValidation.vatCustom") + (100 * row.accounting.vatPercent) + " %";
        break;
    }
    return { type: "default", msg };
  }
}
